import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, delay, takeUntil, map } from 'rxjs/operators';

import { FocusMonitor } from '@angular/cdk/a11y';
import { ConnectedPosition } from '@angular/cdk/overlay';
import {
  Input,
  OnInit,
  Output,
  Component,
  OnDestroy,
  ElementRef,
  HostBinding,
  HostListener,
  EventEmitter,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatListItem } from '@angular/material/list';
import { Router, NavigationEnd } from '@angular/router';

import { SnAbilityService } from '@lib/ability';
import { SnCurrentOrganization } from '@lib/auth/services/current-organization.service';
import { snZoomIn, snZoomOut } from '@lib/common/animations';
import { SnGoogleGeocoderService, SnGoogleGeocoderResult } from '@lib/geo/google/geocoder';
import { t, TranslationService } from '@lib/translation';

import { FsWhoosterDialogComponent } from '../../../../../../whooster/src/lib/components';
import { SnSearchbarOption } from '../../models';
import { SnSearchbarOptionConfig } from '../../types';

@Component({
  selector: 'sn-searchbar',
  templateUrl: './searchbar.component.html',
  styleUrls: ['./searchbar.component.scss'],
  animations: [snZoomIn, snZoomOut],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'sn-searchbar',
  },
})
export class SearchbarComponent implements OnInit, OnDestroy {
  @ViewChildren(MatListItem, { read: ElementRef }) public listOptions: QueryList<
    ElementRef<HTMLElement>
  >;
  @Input()
  set value(value: string) {
    this.searchControl.patchValue(value);
  }

  @Input()
  public placeholder: string = 'Search';

  get translatedPlaceholder(): string {
    return this.placeholder === 'Search' ? t('common.search') : this.placeholder;
  }

  @Input()
  public clearOnNavEnd: boolean = true;

  @Input()
  public debounce: number = 1000;

  @Input()
  public capitalize: boolean = false;

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('options')
  set optionConfigs(value: SnSearchbarOptionConfig[]) {
    this._options = value ? value.map(SnSearchbarOption.fromConfig) : [];
  }

  @Output()
  public readonly valueChange: EventEmitter<string> = new EventEmitter();

  @Output()
  public readonly optionSelect: EventEmitter<SnSearchbarOption> = new EventEmitter();

  @Output()
  public readonly search: EventEmitter<string> = new EventEmitter();

  public readonly searchControl: UntypedFormControl = new UntypedFormControl();

  @HostBinding('class.sn-searchbar--focus')
  public focused = false;

  private _options: SnSearchbarOption[] = [];
  private _canWhooster: boolean;
  private _changeDetector: ChangeDetectorRef;
  private readonly _destroy$ = new Subject<void>();

  public isOpen = false;
  public overlayPosition: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 4,
    },
  ];

  constructor(
    private readonly _router: Router,
    private readonly _matDialog: MatDialog,
    public readonly elementRef: ElementRef<HTMLElement>,
    private readonly _geocoderService: SnGoogleGeocoderService,
    private readonly _currentOrganization: SnCurrentOrganization,
    private readonly _ability: SnAbilityService,
    private readonly _translation: TranslationService,
    focusMonitor: FocusMonitor,
    changeDetectorRef: ChangeDetectorRef,
  ) {
    this._changeDetector = changeDetectorRef;
    focusMonitor
      .monitor(elementRef.nativeElement, true)
      .pipe(delay(1), takeUntil(this._destroy$))
      .subscribe((origin) => {
        this.focused = !!origin;
        changeDetectorRef.markForCheck();
      });
    this._canWhooster = this._ability.can({
      subject: 'integrations/whoostersearch',
      action: ['view'],
    });
  }

  get options(): SnSearchbarOption[] {
    return this._options;
  }

  get hasSearchValue(): boolean {
    return !!this.searchControl.value;
  }

  public ngOnInit(): void {
    this._watchSearchControl();
    this._router.events.pipe(takeUntil(this._destroy$)).subscribe((e) => {
      if (e instanceof NavigationEnd && this.clearOnNavEnd) {
        this.clear();
      }
    });
  }

  public ngOnDestroy(): void {
    this.search.complete();
    this.valueChange.complete();
    this.optionSelect.complete();

    this._destroy$.next();
    this._destroy$.complete();
  }

  public onSubmit(): void {
    this.search.emit(this.searchControl.value);
  }

  public trackOption(index: number, item: SnSearchbarOption): string {
    return item.label;
  }

  public clear(): void {
    this.searchControl.reset(null);
    this.onSubmit();
    this._options = [];
  }

  @HostListener('click', ['$event'])
  public onClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  @HostListener('keyup.arrowdown', ['$event'])
  public scroller(): void {
    const focused = document.activeElement;
    const found = this.listOptions.find((opt) => {
      return opt.nativeElement === focused;
    });
    if (!found && this.listOptions.length > 0) {
      this.listOptions.first.nativeElement.focus();
    }
  }

  @HostListener('keydown.enter', ['$event'])
  public onEnter(): void {
    const focused = document.activeElement;
    const found = this.listOptions.find((opt) => {
      return opt.nativeElement === focused;
    });
    if (!found && this.listOptions.length > 0) {
      this.listOptions.first.nativeElement.focus();
    }

    if (!this.listOptions?.length) {
      this.onSubmit();
    }
  }

  public onOptionClick(option: SnSearchbarOption): void {
    this.optionSelect.emit(option);
    this.searchControl.patchValue(option.value);
    this.onSubmit();
  }

  public onInput(event: any): void {
    const target = event.target;
    const position = target?.selectionStart;
    this.searchControl.patchValue(target?.value?.toUpperCase());
    target.selectionEnd = position;
  }

  public hasIntegrations(): boolean {
    return this._currentOrganization.enabledIntegrations.includes('whooster') && this._canWhooster;
  }

  public whoosterSearch(option: string): void {
    this._addressOptionClickHandler(option);
  }

  private _addressOptionClickHandler(option: string): void {
    this._geocoderService
      .geocode({ address: option })
      .pipe(
        map((places: SnGoogleGeocoderResult[]) => places[0]),
        takeUntil(this._destroy$),
      )
      .subscribe((place: SnGoogleGeocoderResult) => {
        const data = {
          address: place.formattedAddress,
        };
        this._openWhoosterDialog(data);
      });
  }

  private _watchSearchControl(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(this.debounce), distinctUntilChanged(), takeUntil(this._destroy$))
      .subscribe((value) => {
        this.isOpen = this.focused;
        this.valueChange.emit(value || null);
      });
  }

  private _openWhoosterDialog(data: any): void {
    this._matDialog
      .open(FsWhoosterDialogComponent, {
        data,
        disableClose: true,
        backdropClass: 'map__whooster-dialog-backdrop',
        panelClass: 'map__whooster-dialog',
        position: { bottom: '0px', right: '0px' },
      })
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe();
  }
}
